import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Tenant } from '@hsc/types';
import { parseTenantSlugFromHost } from './parse-host.js';

export interface TenantContextValue {
  tenant: Tenant | null;
  slug: string | null;
  loading: boolean;
  error: Error | null;
}

export type TenantFetcher = (slug: string) => Promise<Tenant>;

export const TenantContext = createContext<TenantContextValue | null>(null);

export interface TenantProviderProps {
  children: ReactNode;
  fetcher: TenantFetcher;
  /** Override host detection (mostly for tests / SSR). */
  hostname?: string;
  /** Override initial tenant (for tests / Storybook). */
  initialTenant?: Tenant | null;
}

export function TenantProvider({
  children,
  fetcher,
  hostname,
  initialTenant,
}: TenantProviderProps) {
  const detectedSlug = useMemo(
    () => parseTenantSlugFromHost(hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')),
    [hostname],
  );

  const [tenant, setTenant] = useState<Tenant | null>(initialTenant ?? null);
  const [loading, setLoading] = useState<boolean>(!initialTenant && !!detectedSlug);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (initialTenant || !detectedSlug) return;
    let cancelled = false;
    setLoading(true);
    fetcher(detectedSlug)
      .then((t) => {
        if (!cancelled) setTenant(t);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detectedSlug, fetcher, initialTenant]);

  const value = useMemo<TenantContextValue>(
    () => ({ tenant, slug: detectedSlug, loading, error }),
    [tenant, detectedSlug, loading, error],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
